// @workflow assistant.ui-tools
/**
 * Colours and legend labels for `ui.viewer.color_tags`. Any hex colour is allowed: the model turns
 * a colour the user names into hex, and colours that come from data (task status colours, say)
 * arrive as hex with their label. The colouring legend shows a group's label, else its hex.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { VIEWER_LIMITS, COLOR_LEGEND_TITLE } from './viewerContract.js';

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const SHORT_HEX_LENGTH = 4;

/** C0 controls, DEL and C1 controls: never shown in a label. */
const isControl = (ch) => {
  const code = ch.codePointAt(0);
  return code < 0x20 || (code >= 0x7f && code <= 0x9f);
};

/**
 * `#rgb` or `#rrggbb` (any case, surrounding spaces ignored) as lowercase `#rrggbb`, else null.
 * One spelling per colour, so two groups that name the same colour paint as one style.
 * @param {*} value
 * @returns {string|null}
 */
export function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  const hex = value.trim().toLowerCase();
  if (!HEX_COLOR.test(hex)) return null;
  if (hex.length !== SHORT_HEX_LENGTH) return hex;
  return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
}

/**
 * A legend label: control characters and runs of whitespace become one space, trimmed, cut to
 * the label limit by character (never through an emoji). Null when nothing is left or it isn't text.
 * @param {*} value
 * @returns {string|null}
 */
export function cleanLabel(value) {
  if (typeof value !== 'string') return null;
  const spaced = Array.from(value, (ch) => (isControl(ch) ? ' ' : ch)).join('');
  const label = Array.from(spaced.replace(/\s+/g, ' ').trim()).slice(0, VIEWER_LIMITS.MAX_LABEL_LENGTH).join('').trim();
  return label || null;
}

/**
 * The legend value of each group, in order: its label, else its hex. The legend keys rows by this
 * value, so a label two groups share with different colours names each group's hex too; one label
 * with one colour stays one row.
 * @param {Array<{ color: string, label: string|null }>} groups clean groups (hex normalised)
 * @returns {string[]}
 */
export function legendValues(groups) {
  const base = groups.map((group) => group.label || group.color);
  const colorsByValue = new Map();
  base.forEach((value, i) => {
    if (!colorsByValue.has(value)) colorsByValue.set(value, new Set());
    colorsByValue.get(value).add(groups[i].color);
  });
  return base.map((value, i) => (colorsByValue.get(value).size > 1 ? `${value} (${groups[i].color})` : value));
}

/**
 * The spatial-focus encoding: each legend value paints its own hex; anything else is uncoloured.
 * @param {Array<{ value: string, color: string }>} entries
 * @returns {{ displayName: string, colorFor: (value: *) => string|null }}
 */
export function colorEncoding(entries) {
  const hexByValue = new Map(entries.map((entry) => [entry.value, entry.color]));
  return Object.freeze({
    displayName: COLOR_LEGEND_TITLE,
    colorFor: (value) => hexByValue.get(value) || null,
  });
}
